import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { cx } from './cx';
import { pageList } from './pagination-utils';

export interface PaginationProps {
  /** 1-based */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizes?: readonly number[];
  label?: string;
  className?: string;
  /** i18n */
  text?: Partial<typeof DEFAULT_TEXT>;
}

const DEFAULT_TEXT = {
  previous: 'Previous page',
  next: 'Next page',
  page: (n: number) => `Page ${n}`,
  range: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,
  empty: 'No results',
  perPage: 'Rows per page',
};

const cell =
  'flex h-control-sm min-w-8 items-center justify-center border border-border px-2 text-sm';

/**
 * Pagination (Ace .pagination look): a <nav> landmark, current page marked aria-current="page",
 * every button named ("Page 3", "Next page"). Collapses to prev/next + "x–y of n" on phones.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = [10, 25, 50, 100],
  label = 'Pagination',
  className,
  text,
}: PaginationProps) {
  const t = { ...DEFAULT_TEXT, ...text };
  const sizeId = `ps${useId().replace(/:/g, '')}`;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(total, current * pageSize);

  return (
    <div className={cx('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="text-sm text-ink-muted" aria-live="polite">
        {total === 0 ? t.empty : t.range(from, to, total)}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {onPageSizeChange && (
          <span className="flex items-center gap-2 text-sm">
            <label htmlFor={sizeId} className="text-ink-muted">
              {t.perPage}
            </label>
            <select
              id={sizeId}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-control-sm rounded-ui border border-border-strong bg-surface px-1.5 text-sm"
            >
              {pageSizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </span>
        )}
        <nav aria-label={label}>
          <ul className="flex items-center -space-x-px">
            <li>
              <button
                type="button"
                aria-label={t.previous}
                disabled={current <= 1}
                onClick={() => onPageChange(current - 1)}
                className={cx(
                  cell,
                  'bg-surface-raised text-link enabled:hover:bg-surface-hover enabled:hover:text-link-hover disabled:cursor-not-allowed disabled:text-ink-muted disabled:opacity-60',
                )}
              >
                <ChevronLeft aria-hidden="true" className="size-4" />
              </button>
            </li>
            {pageList(current, pages).map((p, i) =>
              p === 'gap' ? (
                <li
                  key={`gap-${i}`}
                  aria-hidden="true"
                  className={cx(cell, 'hidden bg-surface-raised text-ink-muted sm:flex')}
                >
                  …
                </li>
              ) : (
                <li key={p} className={cx(p !== current && 'hidden sm:block')}>
                  <button
                    type="button"
                    aria-label={t.page(p)}
                    aria-current={p === current ? 'page' : undefined}
                    onClick={() => onPageChange(p)}
                    className={cx(
                      cell,
                      p === current
                        ? 'relative z-10 border-primary bg-primary text-ink-inverse'
                        : 'bg-surface-raised text-link hover:bg-surface-hover hover:text-link-hover',
                    )}
                  >
                    {p}
                  </button>
                </li>
              ),
            )}
            <li>
              <button
                type="button"
                aria-label={t.next}
                disabled={current >= pages}
                onClick={() => onPageChange(current + 1)}
                className={cx(
                  cell,
                  'bg-surface-raised text-link enabled:hover:bg-surface-hover enabled:hover:text-link-hover disabled:cursor-not-allowed disabled:text-ink-muted disabled:opacity-60',
                )}
              >
                <ChevronRight aria-hidden="true" className="size-4" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}
